import { useEffect, useState } from 'react';
import {
  Settings,
  Save,
  Shield,
  Plus,
  Trash2,
  Mail,
  AlertCircle,
  RefreshCw,
  Activity,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-toastify';

const SystemSettings = () => {
  const {
    settings,
    whitelist,
    activityLog,
    activityTotal,
    loading,
    fetchSettings,
    updateSetting,
    fetchWhitelist,
    addToWhitelist,
    removeFromWhitelist,
    fetchActivityLog,
  } = useAdminStore();

  const [activeTab, setActiveTab] = useState('settings');
  const [newEmail, setNewEmail] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [activityPage, setActivityPage] = useState(1);
  const [expandedSettings, setExpandedSettings] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchWhitelist();
    fetchActivityLog({ page: 1, limit: 20 });
  }, []);

  const handleUpdateSetting = async (key) => {
    setProcessing(true);
    try {
      let parsedValue;
      try {
        parsedValue = JSON.parse(editingValue);
      } catch {
        parsedValue = editingValue;
      }
      await updateSetting(key, parsedValue);
      toast.success('Setting updated successfully');
      setEditingKey(null);
      setEditingValue('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update setting');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddToWhitelist = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setProcessing(true);
    try {
      await addToWhitelist(newEmail, newNotes);
      toast.success(`${newEmail} added to admin whitelist`);
      setNewEmail('');
      setNewNotes('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add to whitelist');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveFromWhitelist = async (entry) => {
    if (!confirm(`Remove ${entry.email} from admin whitelist?`)) return;

    try {
      await removeFromWhitelist(entry.id);
      toast.success(`${entry.email} removed from whitelist`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove from whitelist');
    }
  };

  const startEditing = (setting) => {
    setEditingKey(setting.key);
    setEditingValue(JSON.stringify(setting.value, null, 2));
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-green-100 text-green-700';
      case 'update': return 'bg-blue-100 text-blue-700';
      case 'delete': return 'bg-red-100 text-red-700';
      case 'ban': return 'bg-orange-100 text-orange-700';
      case 'unban': return 'bg-teal-100 text-teal-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">System Settings</h2>
          <p className="text-gray-500">Configure platform settings and manage admin access</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b">
          <nav className="flex">
            {[
              { id: 'settings', label: 'Settings', icon: Settings },
              { id: 'whitelist', label: 'Admin Whitelist', icon: Shield },
              { id: 'activity', label: 'Activity Log', icon: Activity },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Platform Configuration</h3>
                <button
                  onClick={() => setExpandedSettings(!expandedSettings)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {expandedSettings ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>

              {expandedSettings && (
                <div className="space-y-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : settings.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Settings className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No settings configured</p>
                    </div>
                  ) : (
                    settings.map((setting) => (
                      <div key={setting.key} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{setting.key}</div>
                            {setting.description && (
                              <div className="text-sm text-gray-500 mt-1">{setting.description}</div>
                            )}
                          </div>
                          {editingKey !== setting.key && (
                            <button
                              onClick={() => startEditing(setting)}
                              className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                            >
                              Edit
                            </button>
                          )}
                        </div>

                        {editingKey === setting.key ? (
                          <div className="mt-3">
                            <textarea
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              rows={3}
                              className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => handleUpdateSetting(setting.key)}
                                disabled={processing}
                                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                              >
                                <Save className="w-4 h-4" />
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingKey(null);
                                  setEditingValue('');
                                }}
                                className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg font-mono text-sm overflow-x-auto">
                            {JSON.stringify(setting.value, null, 2)}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Whitelist Tab */}
          {activeTab === 'whitelist' && (
            <div className="space-y-6">
              {/* Add new admin */}
              <form onSubmit={handleAddToWhitelist} className="border rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-gray-800 mb-4">Add New Admin</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      placeholder="admin@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      value={newNotes}
                      onChange={(e) => setNewNotes(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Product Manager"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!newEmail.trim() || processing}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Add to Whitelist
                </button>
              </form>

              {/* Whitelist table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {whitelist.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                          No admins in whitelist
                        </td>
                      </tr>
                    ) : (
                      whitelist.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="font-medium text-gray-900">{entry.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-500">{entry.notes || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(entry.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleRemoveFromWhitelist(entry)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Activity Log Tab */}
          {activeTab === 'activity' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-800">Admin Activity Log</h4>
                <button
                  onClick={() => fetchActivityLog({ page: activityPage, limit: 20 })}
                  className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {activityLog.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                          <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No activity recorded</p>
                        </td>
                      </tr>
                    ) : (
                      activityLog.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(log.action_type)}`}>
                              {log.action_type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-medium text-gray-900">{log.entity_type}</span>
                            {log.entity_id && (
                              <span className="text-gray-500 ml-1">#{log.entity_id}</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {log.admin_username || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {activityTotal > 20 && (
                <div className="flex items-center justify-center gap-4 pt-4">
                  <button
                    onClick={() => {
                      const newPage = activityPage - 1;
                      setActivityPage(newPage);
                      fetchActivityLog({ page: newPage, limit: 20 });
                    }}
                    disabled={activityPage === 1}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-gray-600">
                    Page {activityPage} of {Math.ceil(activityTotal / 20)}
                  </span>
                  <button
                    onClick={() => {
                      const newPage = activityPage + 1;
                      setActivityPage(newPage);
                      fetchActivityLog({ page: newPage, limit: 20 });
                    }}
                    disabled={activityPage >= Math.ceil(activityTotal / 20)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
